/** The journal: what you have been asked to do, and what has happened.
 *
 * Three sections in a fixed order, because the first question a player opens
 * this for is "what is this scenario asking of me" and the last is "when did
 * that happen". A leaf like every other screen here: it is handed the quests,
 * the chronicle and the condition, and it draws them.
 *
 * ## It is a book, and the book fills the window
 *
 * This screen shipped as three 60px strips stacked at the top of a 1080-row
 * window. Measured on the 1920x1080 shot it took the strips out of, the whole
 * of its content occupied 820x228 - 9% of the screen - and the other 91% was
 * bare backdrop with nothing drawn on it at all. That is the worst kind of
 * empty: not a panel with room in it, but a panel that does not know how big
 * the window is.
 *
 * So it is laid out as an open book: two pages under one gilt head-rule, a
 * spine between them, the left page holding the scenario and its tasks and the
 * right page the chronicle. Both pages are full height whether or not there is
 * anything in them, because a scenario on day one has no quests and no history
 * and the screen has to look finished on day one.
 *
 * ## An empty page is drawn empty on purpose
 *
 * The classic's own quest log on turn one says "You have no current quests" in the
 * middle of an otherwise empty scroll, and the scroll is still a scroll. The
 * two empty wells here do the same thing: a centred line with a rule over and
 * under it, in the middle of the page rather than pinned to its top edge,
 * which is what tells a player the page is empty rather than unfinished.
 */

import { useMemo } from "react";
import type { ContentRegistry } from "@game/core";

import { useAtlas } from "../hooks/useAtlas.js";
import { usePhoneSheetFit } from "../theme/phone.js";
import { usePolishStyles } from "./polish.js";
import { Icon, ScreenStyles } from "./primitives.js";
import { FORM_ART_CSS, PaintedPlate, objectKeyFor } from "./formArt.js";
import { victoryConditionText } from "./GameOver.js";
import type { ChronicleLine, JournalQuest } from "./journalModel.js";
import { questSentence, sortQuests } from "./journalModel.js";
import { StyleLayer, useStyleSheet } from "../theme/styleSheet.js";

export interface JournalProps {
  readonly content: ContentRegistry | null;
  /** The scenario's condition, as `state.victory.condition` holds it. */
  readonly condition: string;
  readonly conditionSubject?: string | null;
  readonly quests: readonly JournalQuest[];
  readonly chronicle: readonly ChronicleLine[];
  /** Centre the map on a quest. Absent on a fixture screen. */
  readonly onGoTo?: ((x: number, y: number, z: number) => void) | undefined;
  readonly onClose: () => void;
}

/** Newest first: a chronicle read top-down would bury today under day one. */
const NEWEST_FIRST = (a: ChronicleLine, b: ChronicleLine): number => b.at - a.at;

/**
 * A page with nothing written on it, said in the middle of the page.
 *
 * This has been round twice and the second answer was worse than the first. It
 * began as ruled paper with a sentence cut into it; that was replaced by a
 * full-bleed town painting with a cartouche floating on it, on the argument
 * that a page of paper is not something to look at. What that produced was a
 * 1000x950 Deepvault cavern carrying one 500x100 message box, and 500 rows of
 * Fenmire swamp under two quest rows - three town paintings stacked in one
 * frame as wallpaper for containers that are empty. It measures well: a
 * decorative backdrop has texture, so the coarse blank-area scan reads the
 * screen as 96% alive. It is still a picture with nothing on it.
 *
 * So the wells are paper again, and the paper is ruled: faint lines at the
 * leading a written entry uses, which is what tells a reader the page is
 * WAITING rather than unfinished. The classic's own quest log on turn one is a scroll
 * with one sentence on it and the scroll is still a scroll. The one painting on
 * this screen is the oath band above, which has the scenario's aim set on it -
 * a picture holding something, rather than three holding nothing.
 */
function EmptyWell(props: {
  readonly lead: string;
  readonly note: string;
}): JSX.Element {
  return (
    <div className="hm-jn__empty">
      <span className="hm-jn__emptylead">{props.lead}</span>
      <span className="hm-jn__emptyrule" aria-hidden="true" />
      <p className="hm-jn__emptynote">{props.note}</p>
    </div>
  );
}

/** A page: ruled paper, set into the card. `ruled` draws the waiting lines
 *  where nothing is written - under the last row, or side to side on an empty
 *  page - and never behind the writing itself. */
function Page(props: {
  readonly empty: boolean;
  readonly children: JSX.Element;
}): JSX.Element {
  return (
    <div className={`hm-jn__well ${props.empty ? "hm-jn__well--ruled" : "hm-jn__well--list"}`}>
      <span className="hm-jn__head" aria-hidden="true" />
      {props.children}
      {props.empty ? null : <span className="hm-jn__fill" aria-hidden="true" />}
    </div>
  );
}

/**
 * The map object a chronicle line is about.
 *
 * This reads the sentence, which is the one thing on this screen that couples
 * two files: `chronicle()` in `journalModel.ts` builds these lines and a
 * `ChronicleLine` carries no kind. The alternative - a kind on the line - is
 * the right shape and is a change to a file this screen does not own, so the
 * coupling is pinned by a test instead: `journal-emblems.spec.ts` runs
 * `chronicle()` over one event of every type it handles and asserts that every
 * line it produces lands on an emblem here. If the wording moves, that fails.
 *
 * Null for a line nothing fits, which draws no socket at all rather than a
 * wrong one.
 */
export function chronicleEmblem(text: string): string | null {
  if (/ takes /.test(text)) return "obj.town_sunspire";
  if (/ falls\./.test(text)) return "obj.obs_gravestones";
  if (/ finds the /.test(text)) return "obj.pu_chest";
  if (/^A .* begins\./.test(text)) return "obj.bld_windmill";
  if (/ is out: /.test(text)) return "obj.obs_grave_cross";
  if (/adjudicated/.test(text)) return "obj.obs_signpost";
  if (/wins the war\.|nobody standing\./.test(text)) return "obj.pu_banner";
  if (/ raised in /.test(text)) return "obj.town_sunspire";
  return null;
}

/** A map object in a cut socket, or nothing at all. */
function QuestArt(props: {
  readonly title: string;
  readonly atlasKey: string | null;
  readonly small?: boolean;
}): JSX.Element | null {
  if (props.atlasKey === null) return null;
  const box = props.small === true ? 40 : 54;
  return (
    <span className={`hm-objsock hm-jn__sock ${props.small === true ? "hm-jn__sock--sm" : ""}`}>
      <Icon
        atlasKey={props.atlasKey}
        size={box}
        boxHeight={box}
        {...(props.title === "" ? {} : { title: props.title })}
      />
    </span>
  );
}

export function Journal(props: JournalProps): JSX.Element {
  usePolishStyles();
  useJournalStyles();
  const atlas = useAtlas();
  const quests = useMemo(() => sortQuests(props.quests), [props.quests]);
  const lines = useMemo(() => [...props.chronicle].sort(NEWEST_FIRST), [props.chronicle]);
  const open = quests.filter((q) => !q.done).length;
  const done = quests.length - open;

  const fit = usePhoneSheetFit();
  return (
    <div className={`hs-screen hm-jn ${fit.screenClass}`.trim()} ref={fit.screenRef}
      style={fit.style} role="region" aria-label="Journal">
      <ScreenStyles />

      <div className="hs-phone-sheet" ref={fit.sheetRef}>
      <div className="hs-screen__bar hs-plank">
        <span className="hs-screen__title">Journal</span>
        <span className="hs-screen__sub hs-muted">
          {open === 0
            ? "Nothing is owed"
            : `${String(open)} open ${open === 1 ? "task" : "tasks"}`}
        </span>
        <span className="hs-spacer" />
        <button type="button" className="hs-plate hs-plate--sm" onClick={props.onClose}>Close</button>
      </div>

      <div className="hs-screen__body hm-jn__body">
        <div className="hm-jn__book">
          {/* -------------------------------------------------- the left page */}
          <div className="hm-jn__page hm-jn__page--left">
            <div className="hs-card hs-wood hm-jn__oath">
              <div className="hs-card__title">This scenario</div>
              {/* The aim is set on the land it is to be won on rather than on
                  another band of the same brown the whole screen is made of.
                  It is the one line on this screen a player opens it for. */}
              <PaintedPlate
                className="hm-jn__oathart"
                atlasKey="town.ironhorde.background"
                focusY={0.38}
              >
                <div className="hm-jn__oathrow">
                  <span className="hm-jn__seal" aria-hidden="true" />
                  <span className="hm-jn__oathtext">
                    <span className="hm-jn__aim">
                      {victoryConditionText(props.condition, props.conditionSubject)}
                    </span>
                    <span className="hm-jn__aimnote">
                      Losing every town and every hero ends it the other way. A kingdom with
                      a hero but no town has seven days to take one.
                    </span>
                  </span>
                </div>
              </PaintedPlate>
            </div>

            <div className="hs-card hs-wood hm-jn__card hm-jn__tasks">
              <div className="hs-card__title">
                {quests.length === 0
                  ? "Tasks"
                  : `Tasks (${String(open)} open${done > 0 ? `, ${String(done)} done` : ""})`}
              </div>
              {/* Two tasks and then the rest of the page. What fills the rest
                  is ruling, not a landscape: a ledger with two entries written
                  in it and room for the next one, which is what this page is. */}
              <Page empty={quests.length === 0}>
                <>
                {quests.length === 0 ? (
                  <EmptyWell
                    lead="Nobody has asked you for anything."
                    note="Seers and quest gates write their demands here when you find them."
                  />
                ) : (
                  <div className="hs-list hm-jn__list">
                    {quests.map((q) => (
                      <div
                        className={`hs-listrow hm-jn__row ${q.done ? "hm-jn__row--done" : ""}`}
                        key={q.id}
                        style={{ cursor: props.onGoTo === undefined ? "default" : "pointer" }}
                        onClick={() => { props.onGoTo?.(q.x, q.y, q.z); }}
                        onKeyDown={(e) => { if (e.key === "Enter") props.onGoTo?.(q.x, q.y, q.z); }}
                        role={props.onGoTo === undefined ? undefined : "button"}
                        tabIndex={props.onGoTo === undefined ? undefined : 0}
                      >
                        {/* The state is a struck tag rather than a coloured
                            word: three states told apart only by hue is the
                            one reading that fails on a dim panel, and this
                            panel is brown on brown. */}
                        {/* The thing itself, drawn the way the map draws it.
                            A quest row is a place a player has to walk to, and
                            "Seer Hut" in 14px serif is a string where the map
                            has a building. The key is resolved off the name the
                            row already prints, so the journal cannot show a
                            different hut from the one on the board. */}
                        <QuestArt title={q.title} atlasKey={objectKeyFor(atlas, q.title)} />
                        <span
                          className={`hm-jn__tag ${
                            q.done ? "hm-jn__tag--done"
                              : q.satisfied === true ? "hm-jn__tag--ready" : "hm-jn__tag--open"}`}
                        >
                          {q.done ? "Done" : q.satisfied === true ? "Ready" : "Open"}
                        </span>
                        <span className="hm-jn__rowtext">
                          <span className="hm-jn__rowtitle">{q.title}</span>
                          <span className="hm-jn__rowask">
                            {questSentence(q.code, props.content)}
                          </span>
                        </span>
                        <span className="hm-jn__where">
                          {`${String(q.x)}, ${String(q.y)}${q.z > 0 ? " · below" : ""}`}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                </>
              </Page>
            </div>
          </div>

          <span className="hm-jn__spine" aria-hidden="true" />

          {/* ------------------------------------------------- the right page */}
          <div className="hm-jn__page hm-jn__page--right">
            <div className="hs-card hs-wood hm-jn__card hm-jn__chron">
              <div className="hs-card__title">
                {lines.length === 0
                  ? "Chronicle"
                  : `Chronicle (${String(lines.length)} ${lines.length === 1 ? "entry" : "entries"})`}
              </div>
              <Page empty={lines.length === 0}>
                <>
                {lines.length === 0 ? (
                  <EmptyWell
                    lead="Nothing has happened yet worth writing down."
                    note="Battles, towns taken and lost, and what each week brought are entered here as they happen."
                  />
                ) : (
                  <div className="hs-list hm-jn__list">
                    {lines.map((l, i) => (
                      <div className="hs-listrow hm-jn__row" key={`${String(l.at)}:${String(i)}`}>
                        <QuestArt title="" atlasKey={chronicleEmblem(l.text)} small />
                        <span className="hm-jn__day">
                          <span className="hm-jn__daynum">{String(l.at)}</span>
                          <span className="hm-jn__dayword">day</span>
                        </span>
                        <span className="hm-jn__rowtext">
                          <span className="hm-jn__rowask">{l.text}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                </>
              </Page>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ styles

const JOURNAL_STYLE_ID = "hm-journal-art";

/** Exported so `journal-layout.spec.ts` can assert the geometry. */
/* The ruling, defined once and used on every part of a page that is waiting to
   be written on.
 *
 * Three layers, and the third is the one that is not decoration. A line every
 * 26px is the leading an entry is set at; a heavier line every seventh of those
 * is a week, which is the unit the chronicle is kept in. Neither survives a
 * measurement of the page: box-average a 1px rule at a 26px pitch down to
 * 20x20 blocks and it contributes about a level and a half, so a fully ruled
 * page reads to a coarse blank-area scan as unbroken flat brown - which is
 * exactly what it looks like from a chair, too. The third layer is the shading
 * an account book puts behind alternate blocks of rows: a slow wave over the
 * same 182px week, light at the head of it and soiled at the foot. It groups
 * the lines into weeks for the eye and it puts real tone across the page at the
 * scale a person actually sees. */
const RULING = `
    repeating-linear-gradient(180deg,
      rgba(0,0,0,0) 0 181px,
      rgba(255,226,176,0.24) 181px 182px,
      rgba(0,0,0,0.38) 182px 183px),
    repeating-linear-gradient(180deg,
      rgba(0,0,0,0) 0 25px,
      rgba(255,226,176,0.155) 25px 26px,
      rgba(0,0,0,0.30) 26px 27px),
    repeating-linear-gradient(180deg,
      rgba(255,224,172,0.075) 0px,
      rgba(255,224,172,0.055) 30px,
      rgba(0,0,0,0.010) 78px,
      rgba(0,0,0,0.075) 122px,
      rgba(0,0,0,0.030) 156px,
      rgba(255,224,172,0.075) 182px)`;

export const JOURNAL_CSS = `
/* The screen behind the book is a desk, not the stone the other panels stand
   on. The shared shell paints a cool blue-grey ground; against 1920x1080 of it
   a brown book reads as a cut-out, and the ground was 91% of what this screen
   drew before it was rewritten. This is the only screen-wide override here and
   it is scoped to this screen's own root class. */
.hs-screen.hm-jn {
  background-color: #17100a;
  background-image:
    radial-gradient(120% 80% at 50% -6%, rgba(120,92,50,0.30) 0%, rgba(0,0,0,0) 62%),
    radial-gradient(90% 70% at 50% 108%, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0) 60%),
    repeating-linear-gradient(96deg, rgba(255,222,176,0.018) 0 2px, rgba(0,0,0,0.05) 2px 6px),
    linear-gradient(174deg, #2c1e12 0%, #1d1409 55%, #0f0a05 100%);
}
/* The body is the book's frame: the book takes the whole of it, less a margin,
   rather than a fixed 820px strip pinned to the top edge. */
.hm-jn__body {
  align-items: stretch;
  justify-content: center;
  padding: 14px 18px 18px;
  min-height: 0;
}
.hm-jn__book {
  display: grid;
  grid-template-columns: minmax(0, 5fr) 26px minmax(0, 6fr);
  gap: 0;
  /* The book takes the frame it is given. A 'min(1400px, 100%)' cap left 260px
     of bare wood down each side of a 1920 window - measured on the shipped
     shot, the two pages spanned x260 to x1659 - which is the same defect this
     screen was rewritten for, at a smaller scale. There is nothing on this
     screen but the book, so there is nothing for the margin to be for. */
  width: 100%;
  min-height: 0;
}
.hm-jn__page {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
  min-width: 0;
}
.hm-jn__page--left { padding-right: 14px; }
.hm-jn__page--right { padding-left: 14px; }
/* The gutter, not a hairline.
   Two cards side by side with a 3px rule between them read as two cards with a
   rule between them. What makes an open book is the FOLD: the leaves curve down
   into it, so the gutter is dark at the centre and the darkness spills a little
   way onto the inside edge of each page. This is 26px of that curve with the
   gilt centre line still in it. */
.hm-jn__spine {
  align-self: stretch;
  background-image:
    linear-gradient(90deg,
      rgba(0,0,0,0.00) 0%,
      rgba(0,0,0,0.42) 26%,
      rgba(0,0,0,0.86) 46%,
      rgba(226,186,104,0.30) 50%,
      rgba(0,0,0,0.86) 54%,
      rgba(0,0,0,0.42) 74%,
      rgba(0,0,0,0.00) 100%);
}
/* A card that owns its page: the whole point of the rewrite. */
.hm-jn__card {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  /* The grain at 0.030/0.04 was four luminance levels of deviation across a
     32x32 block of this card - just under the floor a blank-surface scan uses,
     so the two bands of clear page either side of an empty well's note measured
     as flat surface even though the page around them is ruled. At 0.055/0.075
     the same weave measures seven and the card is still a shade darker than the
     oath above it. Nothing else about the page changed: the ruling, the note
     and the two hairlines are where they were. */
  background-image:
    radial-gradient(88% 55% at 50% 0%, rgba(255,224,176,0.10) 0%, rgba(0,0,0,0) 62%),
    repeating-linear-gradient(63deg, rgba(255,226,182,0.055) 0 3px, rgba(0,0,0,0.075) 3px 7px),
    linear-gradient(178deg, #6a4e2d 0%, #553d22 48%, #3b2915 100%);
}
/* A page with nothing written on it is RULED, not blank.
 *
 * This is the difference between "the journal is empty" and "the journal is
 * unfinished", and on day one - which is when a player first opens it, and the
 * state the screenshot harness shoots - it is the whole of what the two big
 * panels have to say. Faint rules at the leading a written entry uses make an
 * empty page read as a book waiting to be written in.
 *
 * The page is PAPER, and the paper is the last surface on this screen that is
 * not a painting. That is deliberate and it is a reversal: for one round both
 * wells were full-bleed town screens with a message box floating on them, and
 * what that produced was a Deepvault cavern nine hundred and fifty pixels tall
 * carrying one sentence. The paper is worked - two crossed weaves, a lit head
 * and a soiled foot - so that it is a material rather than a fill, but it is
 * paper. */
.hm-jn__well {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 9px 14px 11px 58px;
  position: relative;
  background-color: #241708;
  background-image:
    radial-gradient(120% 64% at 50% 0%, rgba(255,214,150,0.13) 0%, rgba(0,0,0,0) 56%),
    radial-gradient(134% 112% at 50% 106%, rgba(0,0,0,0.58) 0%, rgba(0,0,0,0) 58%),
    repeating-linear-gradient(63deg,
      rgba(255,226,182,0.050) 0 3px, rgba(0,0,0,0.085) 3px 7px),
    repeating-linear-gradient(-27deg,
      rgba(255,226,182,0.030) 0 2px, rgba(0,0,0,0.055) 2px 9px),
    linear-gradient(178deg, #39250f 0%, #2a1a09 56%, #170e05 100%);
  box-shadow:
    inset 0 0 0 1px rgba(0,0,0,0.9),
    inset 0 0 0 2px rgba(216,178,96,0.20),
    inset 0 2px 8px rgba(0,0,0,0.62),
    inset 0 0 60px rgba(0,0,0,0.44);
}
/* The ledger's margin: a red rule with a gilt hairline beside it, down the left
   of every page, at the inset the page's own padding leaves clear. It is what
   separates a ruled page from a striped panel - the eye reads the vertical as
   the edge of a column and the horizontals as lines to write on. */
.hm-jn__well::before {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  left: 44px;
  width: 3px;
  background-image: linear-gradient(90deg,
    rgba(0,0,0,0.55) 0 1px,
    rgba(150,52,34,0.60) 1px 2px,
    rgba(226,186,104,0.22) 2px 3px);
}
/* The oath band above is still a painting, and it is the only one. */
.hm-jn__plate { flex: 1 1 auto; min-height: 0; }
/* The head rule: a gilt hairline over a black one, across the top of the page,
   the way a ledger's first line is ruled heavier than the rest. */
.hm-jn__head {
  flex: 0 0 auto;
  height: 3px;
  margin: 0 2px 8px;
  background-image:
    linear-gradient(180deg,
      rgba(226,186,104,0.62) 0 1px,
      rgba(0,0,0,0.62) 1px 2px,
      rgba(226,186,104,0.24) 2px 3px);
}
/* A page with entries on it: the rows at the top, and the ruling picks up under
   the last of them. When there are more rows than page the list scrolls and the
   ruling collapses to nothing, which is the same page fully written. */
.hm-jn__well--list .hm-jn__list { flex: 0 1 auto; min-height: 0; overflow-y: auto; }
.hm-jn__fill {
  flex: 1 1 auto;
  min-height: 0;
  margin-top: 7px;
  background-image: ${RULING};
}
/* The outside edge of a page in a bound book is a stack of leaves, not a cut.
   Three hairlines stepping out of the frame is the whole of it and it is what
   turns two ruled rectangles into two pages of something. */
.hm-jn__card {
  box-shadow:
    0 0 0 1px rgba(0,0,0,0.85),
    0 2px 0 rgba(255,226,176,0.05),
    0 5px 0 rgba(0,0,0,0.30),
    0 7px 0 rgba(255,226,176,0.04),
    0 10px 0 rgba(0,0,0,0.26),
    0 16px 34px rgba(0,0,0,0.55);
}
/* The oath's band. Tall enough to be a picture, short enough that the tasks
   below it still own the page: at 1080p this is 210px of a 1010px body. */
.hm-jn__oathart {
  flex: 0 0 auto;
  height: clamp(132px, 20vh, 214px);
}
.hm-jn .hm-plate__over { padding: 12px 16px; }
.hm-jn__oathart .hm-plate__over { align-items: stretch; justify-content: center; }
.hm-jn__oathrow {
  padding: 12px 16px 13px;
  background-color: rgba(14,10,5,0.72);
  background-image:
    repeating-linear-gradient(97deg,
      rgba(255,232,190,0.05) 0 1px,
      rgba(0,0,0,0.10) 1px 2px,
      rgba(0,0,0,0) 2px 6px),
    linear-gradient(178deg, rgba(96,70,38,0.50) 0%, rgba(40,28,14,0.62) 100%);
  box-shadow:
    inset 0 0 0 1px rgba(0,0,0,0.9),
    inset 0 0 0 2px rgba(216,178,96,0.30),
    inset 0 2px 0 rgba(255,232,186,0.10),
    0 8px 22px rgba(0,0,0,0.6);
}
/* ---- the map object on a row */
.hm-jn__sock { width: 62px; height: 58px; }
.hm-jn__sock--sm { width: 46px; height: 44px; }
/* The ruling is drawn on the two spacers ABOVE and BELOW the writing, never
   behind it.
 *
 * It used to be one repeating gradient on the whole well, and the two pitches
 * did not agree: the rules step 32px and '.hm-jn__emptynote' set its lines at
 * 12.5px x 1.5 = 18.75px, so a rule crossed a line of type roughly every other
 * line and struck through its x-height. Matching the two pitches is the wrong
 * fix - it locks a paragraph's leading to a decoration - and a wash behind the
 * words is worse, because a pool of lighter paper under a paragraph reads as a
 * halo. A ledger with a note written across it has clear paper where the note
 * is, which is also what the classic's own empty quest log looks like. */
.hm-jn__empty::before,
.hm-jn__empty::after {
  content: "";
  flex: 1 1 auto;
  align-self: stretch;
  min-height: 0;
}
.hm-jn__well--ruled .hm-jn__empty::before,
.hm-jn__well--ruled .hm-jn__empty::after {
  background-image: ${RULING};
}

/* ---- the scenario's aim, given the weight the aim has */
.hm-jn__oath {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  background-image:
    radial-gradient(92% 70% at 50% 0%, rgba(255,226,176,0.14) 0%, rgba(0,0,0,0) 68%),
    linear-gradient(178deg, #7a5a34 0%, #5d4324 52%, #3f2c15 100%);
}
.hm-jn__oathrow { display: flex; align-items: flex-start; gap: 14px; }
/* A wax seal. It is drawn rather than an icon because the shipped set has no
   seal in it, and an aim with nothing beside it reads as a list item. */
.hm-jn__seal {
  flex: 0 0 auto;
  width: 46px; height: 46px;
  border-radius: 50%;
  background-image:
    radial-gradient(circle at 34% 30%, rgba(255,224,190,0.55) 0 14%, rgba(0,0,0,0) 46%),
    radial-gradient(circle at 50% 55%, rgba(0,0,0,0.42) 0 26%, rgba(0,0,0,0) 30%),
    linear-gradient(150deg, #a8341f 0%, #7d2213 52%, #4a1409 100%);
  box-shadow:
    inset 0 0 0 1px rgba(0,0,0,0.6),
    inset 0 -3px 7px rgba(0,0,0,0.5),
    0 2px 6px rgba(0,0,0,0.7);
}
.hm-jn__oathtext { display: flex; flex-direction: column; gap: 5px; min-width: 0; }
.hm-jn__aim {
  font-family: var(--hm-font-display, Georgia, serif);
  font-size: max(23px, var(--hm-type-floor)); /* 23px is 18.4 device px under an 80% page; the 1080p floor is 19 */
  letter-spacing: 0.02em;
  color: #f7e6bb;
  text-shadow: 0 2px 0 #2c1f08, 0 3px 10px rgba(0,0,0,0.8);
}
.hm-jn__aimnote {
  font-size: max(12.5px, var(--hm-type-floor));
  line-height: 1.5;
  color: #bfa87e;
  text-shadow: 0 1px 0 rgba(0,0,0,0.8);
}

/* ---- the two lists */
.hm-jn__list { display: flex; flex-direction: column; gap: 5px; }
.hm-jn__row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 7px 9px;
  background-image: linear-gradient(180deg, rgba(255,226,176,0.05), rgba(0,0,0,0.14));
  box-shadow:
    inset 0 1px 0 rgba(255,226,176,0.09),
    inset 0 -1px 0 rgba(0,0,0,0.45);
}
.hm-jn__row--done { opacity: 0.62; }
.hm-jn__tag {
  flex: 0 0 auto;
  min-width: 58px;
  padding: 2px 7px 3px;
  font-family: var(--hm-font-display, Georgia, serif);
  font-size: max(10px, var(--hm-type-floor));
  letter-spacing: 0.16em;
  text-transform: uppercase;
  text-align: center;
  color: #22170a;
  box-shadow: 0 0 0 1px rgba(0,0,0,0.8), inset 0 1px 0 rgba(255,246,222,0.6);
  text-shadow: 0 1px 0 rgba(255,238,196,0.45);
}
.hm-jn__tag--open { background-image: linear-gradient(180deg, #e6c98d, #a9863b); }
.hm-jn__tag--ready { background-image: linear-gradient(180deg, #bfe08f, #6f9a3c); }
.hm-jn__tag--done {
  background-image: linear-gradient(180deg, rgba(120,104,74,0.55), rgba(52,42,26,0.7));
  color: #d6c49c;
  text-shadow: 0 1px 0 rgba(0,0,0,0.8);
}
.hm-jn__rowtext { display: flex; flex-direction: column; gap: 2px; flex: 1 1 auto; min-width: 0; }
.hm-jn__rowtitle {
  font-family: var(--hm-font-display, Georgia, serif);
  font-size: max(14px, var(--hm-type-floor));
  color: #f3ddab;
  text-shadow: 0 1px 0 rgba(0,0,0,0.85);
}
.hm-jn__rowask { font-size: max(12.5px, var(--hm-type-floor)); line-height: 1.45; color: #c3ab80; }
.hm-jn__where {
  flex: 0 0 auto;
  font-size: max(11px, var(--hm-type-floor));
  letter-spacing: 0.06em;
  color: #8f7c58;
  padding-top: 2px;
}
/* The day is a struck numeral in a cut recess, not a word in a column: a
   chronicle is read by date and the date is the thing the eye jumps down. */
.hm-jn__day {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 46px;
  padding: 2px 0 3px;
  background-image: linear-gradient(180deg, rgba(0,0,0,0.36), rgba(0,0,0,0.12));
  box-shadow: inset 0 1px 0 rgba(0,0,0,0.7), inset 0 -1px 0 rgba(255,224,170,0.08);
}
.hm-jn__daynum {
  font-family: var(--hm-font-display, Georgia, serif);
  font-size: max(16px, var(--hm-type-floor));
  color: #ecc87f;
  text-shadow: 0 1px 0 rgba(0,0,0,0.9);
}
.hm-jn__dayword {
  font-family: var(--hm-font-display, Georgia, serif);
  font-size: max(8px, var(--hm-type-floor));
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #8f7c58;
}

/* ---- an empty page, said in the middle of it */
.hm-jn__empty {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 9px;
  padding: 20px 24px;
  text-align: center;
  min-height: 0;
}
.hm-jn__emptyrule {
  width: min(320px, 70%);
  height: 3px;
  flex: 0 0 auto;
  background-image: linear-gradient(90deg,
    rgba(226,186,104,0) 0%, rgba(226,186,104,0.42) 50%, rgba(226,186,104,0) 100%);
}
.hm-jn__emptylead {
  font-family: var(--hm-font-display, Georgia, serif);
  font-size: max(16px, var(--hm-type-floor));
  color: #f0dcb0; /* was #d8c091, 3.9:1 on the page (118,83,42) */
  text-shadow: 0 1px 0 rgba(0,0,0,0.85);
}
/* #8f7c58 measured 2.67:1 against the page it is printed on - sampled off the
   shipped 1920x1080 shot, ink rgb(143,124,88) on paper rgb(80,57,32). That is
   below the floor at any size, and this is the only sentence on an empty page.
   The lead above it was 5.85:1 and read fine, which is what made the note look
   like a rendering fault rather than a colour choice. */
.hm-jn__emptynote {
  max-width: 44ch;
  font-size: max(12.5px, var(--hm-type-floor));
  line-height: 1.5;
  color: #e6d2a4; /* was #ddc89e, 4.5:1 at the margin */
  text-shadow: 0 1px 0 rgba(0,0,0,0.75);
}

/* ---- 1366x768: the book keeps both pages, and gives up type size for them.
   Stacking the pages would put the chronicle below the fold, which is the
   defect this screen was rewritten for. */
@media (max-height: 880px) {
  .hm-jn__body { padding: 10px 12px 12px; }
  .hm-jn__page { gap: 9px; }
  .hm-jn__page--left { padding-right: 11px; }
  .hm-jn__page--right { padding-left: 11px; }
  .hm-jn__seal { width: 38px; height: 38px; }
  .hm-jn__aim { font-size: max(19px, var(--hm-type-floor)); }
  .hm-jn__aimnote { font-size: max(11.5px, var(--hm-type-floor)); }
  .hm-jn__emptylead { font-size: max(14px, var(--hm-type-floor)); }
  .hm-jn__row { padding: 5px 7px; }
  .hm-jn__sock { width: 50px; height: 46px; }
}
/* The journal's cards are lighter than the kit's (ground 124,93,56 under the heading): the shared gold
   #e8c979 measured 3.7:1 here, so the heading is lifted to the pale gold the classic uses for captions. */
.hm-jn .hs-card__title { color: #f8e6b0; }
`;

const JOURNAL_SHEET = `${FORM_ART_CSS}\n${JOURNAL_CSS}`;

function useJournalStyles(): void {
  useStyleSheet(JOURNAL_STYLE_ID, JOURNAL_SHEET, StyleLayer.screen);
}
